// Partner and campaign attribution cookies, written by the app only once the
// visitor grants Advertisement consent. The /r redirect sets no cookies; it
// hands the app ?ref=<partner code> (utils/referralCode.ts) or
// ?ls_tl=<tracking link id>, and this module turns those into the same
// cookies the server used to set, so the API's attribution middleware
// (cmd/grpc/grpc-web.go) reads them unchanged.
//
// These cookies cannot be HttpOnly: a cookie written from JavaScript never is.
// They hold only a partner code, a tracking link id and two timestamps.
//
// No runtime imports and no DOM access at module load, so node --test can
// load the pure functions directly.
export const AttributionCookie = {
    ReferralCode: '__ls_ref',
    TrackingLinkId: 'ls_tracking_link_id',
    FirstTouchAt: 'ls_first_touch_at',
    LastTouchAt: 'ls_last_touch_at',
};
export const TRACKING_LINK_PARAM = 'ls_tl';
export const ATTRIBUTION_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 3600;
const MAX_TRACKING_LINK_ID_LENGTH = 64;
const TRACKING_LINK_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
// value must already be cookie-safe (encoded by the caller where needed).
export const buildCookie = (name, value, scope, maxAgeSeconds = ATTRIBUTION_COOKIE_MAX_AGE_SECONDS) => {
    let cookie = `${name}=${value}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
    if (scope.domain) {
        cookie += `; Domain=${scope.domain}`;
    }
    if (scope.secure) {
        cookie += '; Secure';
    }
    return cookie;
};
export const cookieNamesIn = (documentCookie) => {
    const names = new Set();
    for (const part of documentCookie.split(';')) {
        const eq = part.indexOf('=');
        const name = (eq === -1 ? part : part.slice(0, eq)).trim();
        if (name) {
            names.add(name);
        }
    }
    return names;
};
// The tracking link id a /r hit appended, or '' when absent or malformed.
export const parseTrackingLinkId = (search) => {
    const id = (new URLSearchParams(search).get(TRACKING_LINK_PARAM) || '').trim();
    if (!id || id.length > MAX_TRACKING_LINK_ID_LENGTH || !TRACKING_LINK_ID_PATTERN.test(id)) {
        return '';
    }
    return id;
};
const paramName = (pair) => {
    const eq = pair.indexOf('=');
    const raw = (eq === -1 ? pair : pair.slice(0, eq)).replace(/\+/g, ' ');
    try {
        return decodeURIComponent(raw);
    }
    catch (_a) {
        return raw;
    }
};
// Drops ls_tl from a query string and leaves every other parameter (?ref in
// particular) byte-for-byte as it was. Returns '' or a string starting '?'.
export const removeTrackingLinkParam = (search) => {
    const query = search.startsWith('?') ? search.slice(1) : search;
    if (!query) {
        return '';
    }
    const kept = query.split('&').filter((pair) => pair && paramName(pair) !== TRACKING_LINK_PARAM);
    return kept.length ? `?${kept.join('&')}` : '';
};
// Latest click wins for the partner code, the tracking link and last touch;
// first touch is kept once set.
export const planAttributionCookies = (capture, existingCookies) => {
    const writes = [];
    if (capture.referralCode) {
        writes.push({ name: AttributionCookie.ReferralCode, value: capture.referralCode });
    }
    if (capture.trackingLinkId) {
        const touchedAt = String(capture.clickedAtSeconds);
        writes.push({ name: AttributionCookie.TrackingLinkId, value: capture.trackingLinkId });
        if (!existingCookies.has(AttributionCookie.FirstTouchAt)) {
            writes.push({ name: AttributionCookie.FirstTouchAt, value: touchedAt });
        }
        writes.push({ name: AttributionCookie.LastTouchAt, value: touchedAt });
    }
    return writes;
};
export const currentCookieScope = () => ({
    domain: REACT_APP_COOKIE_DOMAIN,
    secure: window.location.protocol === 'https:',
});
let capturedTrackingLink = { id: '', clickedAtSeconds: 0 };
// Reads ?ls_tl from the URL the page was loaded with into page memory, then
// removes it from the address bar (?ref and everything else stay).
export const captureTrackingLink = () => {
    const { pathname, search, hash } = window.location;
    const id = parseTrackingLinkId(search);
    if (!id) {
        return;
    }
    capturedTrackingLink = { id, clickedAtSeconds: Math.floor(Date.now() / 1000) };
    window.history.replaceState(window.history.state, '', pathname + removeTrackingLinkParam(search) + hash);
};
// Call only with Advertisement consent.
export const writeAttributionCookies = (referralCode) => {
    const scope = currentCookieScope();
    const writes = planAttributionCookies({
        referralCode,
        trackingLinkId: capturedTrackingLink.id,
        clickedAtSeconds: capturedTrackingLink.clickedAtSeconds,
    }, cookieNamesIn(document.cookie));
    for (const { name, value } of writes) {
        document.cookie = buildCookie(name, value, scope);
    }
};
